import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import IntellendLogo from "@/components/IntellendLogo";

interface InternalRulesBProps {
  onDone?: () => void;
  isBuilderMode?: boolean;
  /** Page ID passed by JourneyPage so the session-storage key is page-scoped. */
  pageId?: number;
}

const InternalRulesB = ({ onDone, isBuilderMode = false, pageId }: InternalRulesBProps) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(0);
  const [isPending, setIsPending] = useState(false);
  // Keep latest onDone in a ref so the retry closure always uses the current value.
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  // In-memory guard: prevents React StrictMode double-mount within a single render cycle
  const hasFiredRef = useRef(false);
  // Session-storage key scoped to this page so reload doesn't re-fire
  const sessionKey = pageId != null ? `internalRulesB_fired_${pageId}` : null;

  const triggerRulesetB = async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/trigger_ruleset_b/`,
        {}
      );
      const data = res.data;

      if (data?.verification_status === "failed") {
        // Backend has set stage to REJECTED; navigate to the reject page.
        const redirectUrl = data?.redirect_url || "/final-reject-state";
        navigate(redirectUrl);
        return;
      }

      // Backend has already advanced the journey stage (success path).
      // Call onDone so JourneyPage re-fetches the new current page.
      if (onDoneRef.current) {
        onDoneRef.current();
      }
    } catch (err) {
      console.error("Failed to trigger ruleset B:", err);
      // On network/server error, retry after a short delay.
      setIsPending(true);
      setTimeLeft(30);
    }
  };

  useEffect(() => {
    if (isBuilderMode) return;
    if (hasFiredRef.current) return;
    // If we already fired for this page in this browser session (e.g. user reloaded
    // after a prefill gate error), do NOT fire again — the journey stage has not
    // changed, so calling onDone() here would skip the gate the user needs to fix.
    if (sessionKey && sessionStorage.getItem(sessionKey)) {
      console.log("[InternalRulesB] Already fired for page", pageId, "— skipping re-fire on reload.");
      return;
    }
    hasFiredRef.current = true;
    if (sessionKey) sessionStorage.setItem(sessionKey, "1");
    triggerRulesetB();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (timeLeft <= 0) {
      if (isPending) {
        setIsPending(false);
        triggerRulesetB();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isPending]);

  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🔍</div>
        <p className="font-semibold text-gray-500">Verification Set B</p>
        <p className="text-xs mt-1">Auto-triggers internal ruleset B verification during the journey.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center items-center p-4">
      <div className="flex flex-col items-center text-center max-w-sm">
        <div className="mb-8">
          <IntellendLogo size="md" />
        </div>

        <div className="relative mb-8">
          <div className="w-16 h-16 border-4 border-intellend-green/20 border-t-intellend-green rounded-full animate-spin"></div>
        </div>

        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          Verifying Your Details
        </h1>
        <p className="text-gray-500 mb-8">
          We are performing a few internal checks to provide you with the best loan offer. This usually takes less than a minute.
        </p>

        {timeLeft > 0 && (
          <div className="bg-slate-50 px-6 py-3 rounded-full border border-slate-100 flex items-center gap-3">
            <span className="flex h-2 w-2 rounded-full bg-intellend-green animate-pulse"></span>
            <span className="text-sm font-medium text-slate-600">
              Checking again in {timeLeft}s
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalRulesB;
